import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import styles from '../components/enquiries/Enquiries.module.css';
import JewellerCard from '../components/community/JewellerCard';
import JewellerProfileModal from '../components/community/JewellerProfileModal';
import { fetchActiveCommunityJewellers } from '../services/communityJewellers';

const CommunityJewellersPage = () => {
  const [jewellers, setJewellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJeweller, setSelectedJeweller] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadJewellers = async () => {
      try {
        const results = await fetchActiveCommunityJewellers();
        if (mounted) {
          setJewellers(results.sort((a, b) => a.companyName.localeCompare(b.companyName)));
        }
      } catch (error) {
        console.error('Error loading community jewellers:', error);
        if (mounted) {
          setJewellers([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadJewellers();

    return () => {
      mounted = false;
    };
  }, []);

  const content = useMemo(() => {
    if (loading) {
      return (
        <div
          style={{
            background: 'var(--Surface-Primary)',
            border: 'var(--card-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            boxShadow: 'var(--shadow-card)',
            color: 'var(--Text-Secondary)',
            fontSize: 'var(--font-size-body3)'
          }}
        >
          Loading live jewellers...
        </div>
      );
    }

    if (!jewellers.length) {
      return (
        <div
          style={{
            background: 'var(--Surface-Primary)',
            border: 'var(--card-border)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            boxShadow: 'var(--shadow-card)',
            color: 'var(--Text-Secondary)',
            fontSize: 'var(--font-size-body3)'
          }}
        >
          No live jewellers to show just yet.
        </div>
      );
    }

    const gridItems = [...jewellers];
    const referralInsertIndex = Math.min(10, gridItems.length);
    gridItems.splice(referralInsertIndex, 0, { id: 'refer-a-jeweller-card', isReferralCard: true });

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--spacing-l)'
        }}
      >
        {gridItems.map((item) => {
          if (item.isReferralCard) {
            return (
              <Link
                key={item.id}
                to="/refer-jeweller"
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <article
                  style={{
                    minHeight: '100%',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--spacing-xl)',
                    background: 'linear-gradient(145deg, #f3ecff 0%, #e5d8ff 52%, #f7f2ff 100%)',
                    border: '1px solid rgba(110, 66, 255, 0.14)',
                    color: 'var(--Text-Primary)',
                    boxShadow: 'var(--shadow-frame-big)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 'var(--spacing-l)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.transform = 'translateY(-3px)';
                    event.currentTarget.style.boxShadow = 'var(--shadow-popup)';
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.transform = 'translateY(0)';
                    event.currentTarget.style.boxShadow = 'var(--shadow-frame-big)';
                  }}
                >
                  <div>
                    <h3
                      style={{
                        margin: '0 0 var(--spacing-s) 0',
                        fontSize: 'var(--font-size-subtitle1)',
                        fontWeight: 'var(--font-weight-subtitle1)',
                        color: 'var(--Text-Primary)'
                      }}
                    >
                      Know a jeweller who should join?
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 'var(--font-size-body3)',
                        lineHeight: '1.6',
                        color: 'var(--Text-Secondary)'
                      }}
                    >
                      Recommend a jeweller to Boutee and help grow the community with people you trust.
                    </p>
                  </div>

                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 'fit-content',
                      padding: '12px 18px',
                      borderRadius: '999px',
                      background: 'var(--Text-Primary)',
                      border: '1px solid var(--Text-Primary)',
                      color: 'var(--Text-Contrast)',
                      fontSize: 'var(--font-size-body4)',
                      fontWeight: 'var(--font-weight-medium)'
                    }}
                  >
                    Refer a jeweller
                  </div>
                </article>
              </Link>
            );
          }

          return (
            <JewellerCard
              key={item.id}
              jeweller={item}
              onClick={setSelectedJeweller}
            />
          );
        })}
      </div>
    );
  }, [jewellers, loading]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-m)'
          }}
        >
          <Link
            to="/community"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-xxs)',
              color: 'var(--Text-Secondary)',
              textDecoration: 'none',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            <ChevronLeftIcon style={{ width: '16px', height: '16px' }} />
            <span>Back to Community</span>
          </Link>

          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              <h1 className={styles.headerTitle}>All jewellers on Boutee</h1>
              <p className={styles.headerSubtitle}>
                Explore the jewellers currently live on Boutee, learn more about their work, and get a better feel for the wider community.
              </p>
            </div>
          </div>
        </div>
      </div>

      {content}

      {selectedJeweller && (
        <JewellerProfileModal
          jeweller={selectedJeweller}
          onClose={() => setSelectedJeweller(null)}
        />
      )}
    </div>
  );
};

export default CommunityJewellersPage;
